// Public standalone newsletter page — dedicated landing for subscribers.
// Reached via footer links, direct URL, and post-contact-form redirect
// (?contact_sent=1 shows a banner confirming the contact message was
// received before inviting the visitor to stay in the loop).
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Layout from "@/components/Layout";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useT } from "@/lib/i18n";

const NewsletterPage = () => {
  const t = useT();
  const [params] = useSearchParams();
  const contactSent = params.get("contact_sent") === "1";

  return (
    <Layout>
      <Helmet>
        <title>{t("newsletterPage.meta.title")}</title>
        <meta name="description" content={t("newsletterPage.meta.desc")} />
      </Helmet>
      <section className="py-16 md:py-24 bg-[color:var(--gv-mist)] min-h-[70vh]">
        <div className="max-w-2xl mx-auto px-6">
          {contactSent && (
            <div
              className="mb-8 bg-white border-l-4 border-[color:var(--gv-pink)] rounded-[5px] p-5 flex items-start gap-3 shadow-sm"
              data-testid="newsletter-contact-sent-banner"
            >
              <CheckCircle2 size={22} className="text-[color:var(--gv-pink)] shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg text-[color:var(--gv-ink)] mb-1">
                  {t("newsletterPage.contactSent.title")}
                </div>
                <p className="text-[13.5px] text-[color:var(--gv-ink)]/70 leading-relaxed">
                  {t("newsletterPage.contactSent.desc")}
                </p>
              </div>
            </div>
          )}

          <div className="text-center mb-10">
            <div className="eyebrow mb-3">{t("newsletterPage.eyebrow")}</div>
            <h1
              className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-3"
              data-testid="newsletter-page-title"
            >
              {t("newsletterPage.title")}
            </h1>
            <p className="text-[color:var(--gv-ink)]/70 text-[15px] leading-relaxed max-w-lg mx-auto">
              {t("newsletterPage.subtitle")}
            </p>
          </div>

          <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6 md:p-8">
            <div className="flex items-center gap-2 mb-5 text-[color:var(--gv-ink)]/70">
              <Mail size={16} className="text-[color:var(--gv-pink)]" />
              <span className="text-[11.5px] uppercase tracking-widest">
                {t("newsletterPage.form.eyebrow")}
              </span>
            </div>
            <NewsletterSignup source="page" variant="page" />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsletterPage;
